import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Search as SearchIcon, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";
import { ACTIVITY_TYPES } from "@pages/Contents/V2/Corporate/Catalog/AddActivity/config/activityTypes";
import { mapEntityToCard } from "@pages/Contents/V2/Corporate/Catalog/mapEntityToCard";

const PAGE_SIZE = 16;

export type LOPickerEntityType = "activity" | "course";

export interface LOPickerSelectedItem {
  id: number;
  title: string;
  type: string;
  entity: Record<string, any>;
}

interface LOPickerCstProps {
  open: boolean;
  onClose: () => void;
  title: string;
  entityType: LOPickerEntityType;
  excludeIds: number[];
  onConfirm: (added: LOPickerSelectedItem[]) => void;
  onCreateNew?: () => void;
}

const LOPickerCst: React.FC<LOPickerCstProps> = ({
  open,
  onClose,
  title,
  entityType,
  excludeIds,
  onConfirm,
  onCreateNew,
}) => {
  const { t } = useTranslation("lo-picker");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const { t: tActivity } = useTranslation("wizard-activity");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchSubmitted, setSearchSubmitted] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");

  const [selectedMap, setSelectedMap] = useState<Map<number, any>>(new Map());

  const url = useMemo(() => {
    if (!corporateId) return null;
    if (entityType === "course") {
      return ServicesURL.getCoursesAssociation.replace(
        ":idCorporate",
        corporateId,
      );
    }
    return ServicesURL.getActivities.replace(":idCorporate", corporateId);
  }, [corporateId, entityType]);

  const fieldFilters = useMemo(() => {
    const f: Record<string, string> = {};
    if (statusFilter) f.status = statusFilter;
    if (typeFilter && entityType === "activity") f.type = typeFilter;
    return f;
  }, [statusFilter, typeFilter, entityType]);

  const { items, totalItems, totalPages, isLoading } = usePaginatedList<
    Record<string, any>
  >(url, {
    page,
    pageSize: PAGE_SIZE,
    search: searchSubmitted || undefined,
    fieldFilters,
  });

  useEffect(() => {
    if (!open) return;
    setSelectedMap(new Map());
    setPage(1);
    setSearchInput("");
    setSearchSubmitted("");
    setStatusFilter("");
    setTypeFilter("");
  }, [open]);

  const visibleItems = useMemo(() => {
    if (!excludeIds || excludeIds.length === 0) return items;
    const exclude = new Set(excludeIds);
    return items.filter((it) => !exclude.has(Number(it.id)));
  }, [items, excludeIds]);

  const handleSubmitSearch = () => {
    setSearchSubmitted(searchInput.trim());
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmitSearch();
    }
  };

  const handleSelectToggle = (entity: any, next: boolean) => {
    setSelectedMap((prev) => {
      const copy = new Map(prev);
      if (next) copy.set(Number(entity.id), entity);
      else copy.delete(Number(entity.id));
      return copy;
    });
  };

  const handleConfirm = () => {
    const added: LOPickerSelectedItem[] = Array.from(selectedMap.entries()).map(
      ([id, entity]) => ({
        id,
        title: entity.title || entity.name || `#${id}`,
        type: entity.type || entity.learning_object_type || "",
        entity,
      }),
    );
    onConfirm(added);
    onClose();
  };

  const statusOptions = [
    { value: "", label: t("status.all") },
    { value: "DRAFT", label: t("status.draft") },
    { value: "PUBLISHED", label: t("status.published") },
  ];

  const typeOptions = useMemo(
    () => [
      { value: "", label: t("type.all") },
      ...ACTIVITY_TYPES.map((td) => ({
        value: td.key,
        label: tActivity(td.titleI18nKey, { defaultValue: td.key }) || td.key,
      })),
    ],
    [t, tActivity],
  );

  const canConfirm = selectedMap.size > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "12px 24px 16px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
          pt: 20,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            mt: "10px",
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{ flex: "1 1 240px", minWidth: 0 }}
            onKeyDown={handleSearchKeyDown}
          >
            <InputCst
              id="lo-picker-search"
              label={t("search-placeholder") || ""}
              value={searchInput}
              onChange={(e: any) => setSearchInput(e.target.value)}
              endAdornment={
                <SearchIcon size={16} color={theme.palette.text.tertiary} />
              }
            />
          </Box>
          <Box sx={{ flex: "0 0 160px" }}>
            <SelectCst
              id="lo-picker-status"
              label={t("status.label") || ""}
              value={statusFilter}
              onChange={(v: any) => {
                setStatusFilter(String(v || ""));
                setPage(1);
              }}
              options={statusOptions}
            />
          </Box>
          {entityType === "activity" && (
            <Box sx={{ flex: "0 0 180px" }}>
              <SelectCst
                id="lo-picker-type"
                label={t("type.label") || ""}
                value={typeFilter}
                onChange={(v: any) => {
                  setTypeFilter(String(v || ""));
                  setPage(1);
                }}
                options={typeOptions}
              />
            </Box>
          )}
          {onCreateNew && (
            <Button
              onClick={onCreateNew}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
                flexShrink: 0,
              }}
            >
              {t("create-new")}
            </Button>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography
            sx={{ fontSize: "13px", color: theme.palette.text.tertiary }}
          >
            {t("selected-count", { count: selectedMap.size })}
          </Typography>
          {/* TODO: BE non supporta sort diversi da "più recente", cablare qui un
              SelectCst quando sarà possibile */}
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: theme.palette.primary.main,
            }}
          >
            {t("sort.most-recent")}
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : visibleItems.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 6,
              color: theme.palette.text.tertiary,
              fontSize: "13px",
            }}
          >
            {t("empty")}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {visibleItems.map((entity) => {
              const id = Number(entity.id);
              const selected = selectedMap.has(id);
              const cardProps = mapEntityToCard(entity, entityType, {
                t: tCatalog,
                onDetails: () => {
                  // TODO: pagina dettaglio v2 non disponibile oggi.
                  console.info("[lo-picker] details — TODO", entityType, id);
                },
              });
              return (
                <ListItemCardCst
                  key={id}
                  {...cardProps}
                  selectable
                  selected={selected}
                  onSelect={(next) => handleSelectToggle(entity, next)}
                />
              );
            })}
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <PaginatorCst
              page={page}
              pageSize={PAGE_SIZE}
              totalItems={totalItems}
              onPageChange={setPage}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px 20px", gap: 2 }}>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!canConfirm}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": { backgroundColor: theme.palette.primary.dark },
          }}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LOPickerCst;
